import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Form, notification, Typography } from 'antd';
import { useDefaultFormErrorNotification, useDisclosure } from '@/hooks';
import {
  useCreatePart,
  usePart,
} from '@/core/domain/operational/Parts/Parts.hooks';
import { PageProps } from '@/types/PageProps';
import PartForm, {
  PartFormData,
} from '@/components/Operational/Inventory/Parts/PartForm';
import TabContentWrapper from '@/components/_common/TabContentWrapper';
import { SaveButton } from '@/components/_common/Buttons';
import {
  CreatePartRequest,
  PartFlag,
} from '@/core/domain/operational/Parts/Parts.types';
import { useEffect, useState } from 'react';
import { createUploadFileObj } from '@/core/domain/archive/Archive/Archive.utils';
import { AxiosError } from 'axios';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import CreatePartByFragaModal from '@/components/Operational/Inventory/Parts/CreatePartByFragaModal';

function CreatePartPage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();
  const [form] = Form.useForm<PartFormData>();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const [partId, setPartId] = useState<number>();
  const [modalSelectItemFraga, modalSelectItemFragaActions] = useDisclosure();
  const productId = Form.useWatch('catalogProductId', form);

  const queryParams = new URLSearchParams(useLocation().search);
  const createMutation = useCreatePart();
  const partDetail = usePart(partId || 0);

  useEffect(() => {
    if (history.location.search) {
      const params = new URLSearchParams(history.location.search);
      const partIdParams = params.get('partId');

      if (partIdParams) {
        setPartId(Number(partIdParams));
      }
    }
  }, [history.location.search]);

  useEffect(() => {
    if (partDetail.data) {
      const { imageUrl, ...rest } = partDetail.data;

      const img = createUploadFileObj(imageUrl);

      const formObj: PartFormData = {
        ...rest,
        manufacturerId: rest.manufacturer.id,
        subGroupId: rest.subGroup?.id,
        imageUrl: img ? [img] : [],
        barCode: '',
        sku: '',
      };

      form.setFieldsValue(formObj);
    }
  }, [partDetail.data]);

  const handleSaveClick = async () => {
    try {
      const values = await form.validateFields();

      const obj: CreatePartRequest = {
        ...values,
        copyId: +Object.fromEntries(queryParams).partId || undefined,
        flag: PartFlag.newPart,
        imageUrl: values.imageUrl?.length
          ? values.imageUrl[0].response?.filePath || values.imageUrl[0].url
          : undefined,
      };

      const result = await createMutation.mutateAsync(obj);

      notification.success({
        message: intl.formatMessage({
          id: 'parts.create.notifications.success.title',
        }),
        description: intl.formatMessage({
          id: 'parts.create.notifications.success.description',
        }),
      });

      history.push(`/app/operational/inventory/parts/${result.id}`);
    } catch (err) {
      const error = err as AxiosError;

      if (!error.isAxiosError) {
        sendFormErrorNotification(err);
      }
    }
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: 'parts.create' })} />

      <TabContentWrapper
        title={route.name}
        extra={
          <SaveButton
            block
            type="primary"
            onClick={handleSaveClick}
            loading={createMutation.isLoading}
          >
            <FormattedMessage id="commons.entity.save" />
          </SaveButton>
        }
      >
        {!!partId && (
          <Typography.Title level={4} type="success" style={{ marginTop: 0 }}>
            Esta peça está sendo replicada
          </Typography.Title>
        )}

        <PartForm isLoading={false} form={form} withInitialStock />

        <Divider />

        <SpaceForButton>
          <ButtonBack path="/app/operational/inventory/parts" />

          <Button type="primary" onClick={modalSelectItemFragaActions.open}>
            Selecionar Peça da Fraga
          </Button>
        </SpaceForButton>
      </TabContentWrapper>

      <CreatePartByFragaModal
        open={modalSelectItemFraga}
        onCancel={modalSelectItemFragaActions.close}
        onCallbackValuesForm={(values) => {
          if (values) {
            form.setFieldsValue({ ...values });
          }
        }}
        productId={productId}
      />
    </>
  );
}

export default CreatePartPage;
